// Product (sourcing item) detail: breadcrumb (Store > Category > Family > Product),
// gallery with carousel, full details, Add to quotation + View cart buttons

import { useParams, Link } from 'react-router-dom';
import { useSourcingItems, useMoqUnits, useBrands, useOrigins, useItemFamilyCategory, useCategories, useProductFamilies } from '@/hooks/use-store';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import SEO from '@/components/SEO';
import { toAbsoluteImageUrl } from '@/lib/store-images';
import { SITE_URL, sitePath } from '@/lib/site-config';
import { ALL_LATAM_PORTS, LATAM_COUNTRIES } from '@/lib/constants';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { ShoppingCart, Package, MapPin, Tag, Box, Clock, FileText, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { AddToQuotationContainerModal } from '@/components/store/AddToQuotationContainerModal';
import { getWorkerApi } from '@/lib/worker-api';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';

const StoreProduct = () => {
  const { itemSlug } = useParams<{ itemSlug: string }>();
  const { data: items } = useSourcingItems(null);
  const { data: moqUnits } = useMoqUnits();
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: categories } = useCategories();
  const { data: families } = useProductFamilies(null);
  const { data: itemContext } = useItemFamilyCategory(items?.find((i) => i.slug === itemSlug)?.id ?? null);

  const item = items?.find((i) => i.slug === itemSlug);
  /** 'pending' = loading/resolving, 'not_found' = resolve API returned 404. Never show "Producto no encontrado" until we know. */
  const [resolveStatus, setResolveStatus] = useState<'pending' | 'not_found'>(() => (itemSlug ? 'pending' : 'not_found'));
  const [open, setOpen] = useState(false);

  // When we have item, clear pending. When item missing and slug present, ask worker if old_slug → redirect. Only set not_found on 404 so we never flash "no encontrado" before redirect.
  useEffect(() => {
    if (item || !itemSlug?.trim()) {
      setResolveStatus('not_found');
      return;
    }
    let cancelled = false;
    getWorkerApi<{ redirect: string }>(`/api/resolve-product-slug?slug=${encodeURIComponent(itemSlug)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.ok && res.data.redirect) {
          window.location.replace(res.data.redirect);
          return;
        }
        setResolveStatus('not_found');
      })
      .catch(() => {
        if (!cancelled) setResolveStatus('not_found');
      });
    return () => { cancelled = true; };
  }, [item, itemSlug]);
  const [quantity, setQuantity] = useState(item ? String(Math.max(Math.round(Number(item.moq_value) || 1), 1)) : '');
  const { add, request } = useQuotationCart();
  const { toast } = useToast();
  const [containerModalOpen, setContainerModalOpen] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{ line: QuotationCartLine; item: NonNullable<typeof item> } | null>(null);

  const moqUnit = item ? moqUnits?.find((u) => u.id === item.moq_unit_id) : null;
  const brand = item ? brands?.find((b) => b.id === item.brand_id) : null;
  const origin = item ? origins?.find((o) => o.id === item.origin_id) : null;
  const category = itemContext?.categoryId ? categories?.find((c) => c.id === itemContext.categoryId) : null;
  const family = itemContext?.familyId ? families?.find((f) => f.id === itemContext.familyId) : null;

  // Image gallery data (must be before early return to satisfy rules of hooks)
  const coverUrl = item?.packshot_image ?? null;
  const secondaryUrls = item?.secondary_images ?? [];
  
  // All images combined: cover first, then secondary images
  const allImages = useMemo(() => {
    const imgs: string[] = [];
    if (coverUrl) imgs.push(coverUrl);
    imgs.push(...secondaryUrls);
    return imgs;
  }, [coverUrl, secondaryUrls]);

  // Gallery state (hooks must be called before any conditional returns)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    ...(category ? [{ label: category.name, path: `/store/category/${category.slug}` }] : []),
    ...(family ? [{ label: family.family_name, path: category ? `/store/category/${category.slug}/${family.slug}` : undefined }] : []),
    { label: item?.internal_name ?? itemSlug ?? 'Product' },
  ];

  const handleAddToQuotation = () => {
    if (!item) return;
    const q = Number(quantity);
    const minMoq = Math.round(Number(item.moq_value) || 1);
    if (Number.isNaN(q) || q < minMoq) {
      toast({ title: 'Cantidad inválida', description: `Mínimo ${minMoq} ${moqUnit?.name ?? ''}`, variant: 'destructive' });
      return;
    }
    const line: QuotationCartLine = {
      sourcingItemId: item.id,
      slug: item.slug,
      name: item.internal_name,
      moqValue: Math.round(Number(item.moq_value) || 1),
      moqUnitId: item.moq_unit_id ?? '',
      moqUnitLabel: moqUnit?.name ?? '',
      quantity: q,
      salesFormat: item.sales_format,
      imageUrl: item.packshot_image,
      brandName: brand?.name,
      originName: origin?.name,
      unitsPerBox: item.units_per_box ?? undefined,
      boxesPerPallet: item.boxes_per_pallet ?? undefined,
      boxesPerContainer: item.boxes_per_container ?? undefined,
    };
    setPendingAdd({ line, item });
    setContainerModalOpen(true);
  };

  if (!item) {
    const stillLoading = items === undefined || resolveStatus === 'pending';
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        {stillLoading ? (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden />
            <p className="text-sm text-zinc-600">Cargando producto…</p>
          </>
        ) : (
          <>
            <p className="text-zinc-600">Este producto no está disponible.</p>
            <Button asChild variant="outline">
              <Link to="/store">Volver al store</Link>
            </Button>
          </>
        )}
      </div>
    );
  }

  const observaciones = item.observaciones?.trim();

  const hasMultipleImages = allImages.length > 1;
  const currentImage = allImages[currentImageIndex] ?? item.packshot_image;

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  // --- pSEO: title, description, H1 from seo_metadata (AI-generated or manual) with fallbacks ---
  const seoMeta = item.seo_metadata ?? {};
  const canonicalUrl = sitePath(`/store/product/${item.slug}`);

  const fallbackTitle = `${item.internal_name} al por mayor | B2B LATAM | Padmora`;
  const seoTitle = (typeof seoMeta.meta_title === 'string' && seoMeta.meta_title.trim())
    ? seoMeta.meta_title
    : fallbackTitle;

  const fallbackDescription = [
    item.internal_name,
    item.sales_format && `formato ${item.sales_format}`,
    brand?.name && `marca ${brand.name}`,
  ].filter(Boolean).join(', ') + '. Pedido mínimo y cotización B2B en Padmora.';
  const seoDescription = (typeof seoMeta.meta_description === 'string' && seoMeta.meta_description.trim())
    ? seoMeta.meta_description
    : fallbackDescription;

  // H1 derives from pSEO title (without "| Padmora" suffix) or a B2B-oriented fallback
  const seoH1 = (typeof seoMeta.meta_title === 'string' && seoMeta.meta_title.trim())
    ? seoMeta.meta_title.replace(/\s*\|\s*Padmora.*$/i, '')
    : `${item.internal_name} al por mayor para distribuidores B2B`;

  const seoBreadcrumbs: Array<{ name: string; url: string }> = [
    { name: 'Store', url: sitePath('/store') },
    ...(category ? [{ name: category.name, url: sitePath(`/store/category/${category.slug}`) }] : []),
    ...(family && category ? [{ name: family.family_name, url: sitePath(`/store/category/${category.slug}/${family.slug}`) }] : []),
    { name: item.internal_name, url: canonicalUrl },
  ];

  const productImage = toAbsoluteImageUrl(item.packshot_image);

  // Dynamic offers description using product name + assigned ports (avoids boilerplate across 400 products)
  const assignedPortsText = seoMeta.assigned_ports?.length
    ? seoMeta.assigned_ports.slice(0, 3).join(', ')
    : 'Latinoamérica y el Caribe';
  const offerDescription = `Hecho a pedido (made to order). ${item.internal_name}: precio bajo consulta para compras al por mayor B2B. Importación por contenedor FOB/CIF a ${assignedPortsText}.`;

  const productStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Padmora',
        url: SITE_URL,
        logo: sitePath('/android-chrome-512x512.png'),
      },
      {
        '@type': 'Product',
        name: item.internal_name,
        description: seoDescription,
        image: productImage ?? undefined,
        url: canonicalUrl,
        ...(brand && { brand: { '@type': 'Brand', name: brand.name } }),
        ...(item.barcode_ean && { gtin13: item.barcode_ean }),
        offers: {
          '@type': 'Offer',
          url: canonicalUrl,
          price: '0',
          availability: 'https://schema.org/PreOrder',
          itemCondition: 'https://schema.org/NewCondition',
          priceCurrency: 'USD',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'USD',
            description: offerDescription,
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: LATAM_COUNTRIES.map((code) => ({
              '@type': 'DefinedRegion',
              addressCountry: code,
            })),
          },
        },
      },
      ...(seoMeta.ai_faqs && seoMeta.ai_faqs.length > 0
        ? [
            {
              '@type': 'FAQPage',
              mainEntity: seoMeta.ai_faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.pregunta,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.respuesta,
                },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div className="space-y-6">
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={productImage ?? undefined}
        canonical={canonicalUrl}
        breadcrumbs={seoBreadcrumbs}
        structuredData={productStructuredData}
      />
      <StoreBreadcrumbs segments={breadcrumbs} />
      <div className="grid gap-8 lg:grid-cols-[480px_1fr]">
        {/* Gallery: bigger image with carousel navigation */}
        <div className="space-y-3">
          {/* Main Image Display */}
          {currentImage && (
            <div className="relative group">
              <div
                className="flex items-center justify-center min-h-[350px] w-full overflow-hidden rounded-xl border border-zinc-200 bg-white p-4 cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={currentImage}
                  alt={item.internal_name}
                  className="max-h-[450px] max-w-full w-auto h-auto object-contain transition-transform duration-200 group-hover:scale-105"
                />
              </div>
              
              {/* Zoom indicator */}
              <div className="absolute top-3 right-3 bg-black/50 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-3 w-3" />
                Click para ampliar
              </div>

              {/* Navigation arrows (if multiple images) */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToPrevImage(); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-zinc-200 shadow-md flex items-center justify-center hover:bg-white transition-colors"
                    aria-label="Imagen anterior"
                  >
                    <ChevronLeft className="h-5 w-5 text-zinc-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToNextImage(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-zinc-200 shadow-md flex items-center justify-center hover:bg-white transition-colors"
                    aria-label="Imagen siguiente"
                  >
                    <ChevronRight className="h-5 w-5 text-zinc-700" />
                  </button>
                </>
              )}

              {/* Image counter */}
              {hasMultipleImages && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              )}
            </div>
          )}

          {/* Thumbnail strip (if multiple images) */}
          {hasMultipleImages && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {allImages.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImageIndex(i)}
                  className={`h-20 w-20 flex items-center justify-center overflow-hidden rounded-lg border-2 bg-white shrink-0 transition-all ${
                    i === currentImageIndex
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <img src={url} alt="" className="max-h-full max-w-full w-auto h-auto object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lightbox Modal for full-size image viewing */}
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
            <div className="relative w-full h-[90vh] flex items-center justify-center">
              {/* Close button */}
              <button
                onClick={() => setLightboxOpen(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                aria-label="Cerrar"
              >
                <X className="h-6 w-6 text-white" />
              </button>

              {/* Main image in lightbox */}
              <img
                src={currentImage}
                alt={item.internal_name}
                className="max-h-full max-w-full object-contain"
              />

              {/* Navigation in lightbox (if multiple images) */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={goToPrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    aria-label="Imagen anterior"
                  >
                    <ChevronLeft className="h-7 w-7 text-white" />
                  </button>
                  <button
                    onClick={goToNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    aria-label="Imagen siguiente"
                  >
                    <ChevronRight className="h-7 w-7 text-white" />
                  </button>
                </>
              )}

              {/* Image counter in lightbox */}
              {hasMultipleImages && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              )}

              {/* Thumbnail strip in lightbox */}
              {hasMultipleImages && (
                <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 px-4 py-2 bg-black/40 rounded-lg max-w-[90vw] overflow-x-auto">
                  {allImages.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`h-14 w-14 flex items-center justify-center overflow-hidden rounded-md shrink-0 transition-all ${
                        i === currentImageIndex
                          ? 'ring-2 ring-white'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={url} alt="" className="max-h-full max-w-full w-auto h-auto object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        {/* Details Section: sticky product block (Shopify-style) + scrollable content below */}
        <div className="space-y-6">
          {/* Sticky block: title, info card, and CTAs stay visible while rest of page scrolls */}
          <div className="sticky top-4 z-10 space-y-6 bg-background pb-2">
            {/* Product Title and Format */}
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">{seoH1}</h1>
              {item.sales_format && (
                <p className="text-zinc-600 mt-1">{item.sales_format}</p>
              )}
            </div>

            {/* Key Product Information - Highlighted Card */}
            <div className="rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
            {/* Pedido Mínimo (MOQ) - Most important info, prominent display */}
            {item.moq_value != null && (
              <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border-b border-emerald-100">
                <Package className="h-5 w-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="text-xs text-emerald-700 font-medium">Pedido Mínimo</p>
                  <p className="text-lg font-bold text-emerald-900">
                    {Math.round(Number(item.moq_value) || 0).toLocaleString()} {moqUnit?.name ?? 'unidades'}
                  </p>
                </div>
              </div>
            )}

            {/* Information Grid */}
            <div className="grid sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-zinc-100">
              {/* Brand */}
              {brand && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <Tag className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-500">Marca</p>
                    <p className="text-sm font-semibold text-zinc-900">{brand.name}</p>
                  </div>
                </div>
              )}

              {/* Origin */}
              {origin && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <MapPin className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-500">Origen</p>
                    <p className="text-sm font-semibold text-zinc-900">{origin.name}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Logistics Information */}
            {((item.units_per_box != null && item.units_per_box > 0) ||
              (item.boxes_per_pallet != null && item.boxes_per_pallet > 0) ||
              (item.boxes_per_container != null && item.boxes_per_container > 0) ||
              (item.pallets_per_container != null && item.pallets_per_container > 0) ||
              item.recommended_container_type ||
              (item.production_time_days != null)) && (
              <div className="border-t border-zinc-100 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="h-4 w-4 text-zinc-400" />
                  <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">Información Logística</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {item.units_per_box != null && item.units_per_box > 0 && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500">Unidades por caja</p>
                      <p className="font-semibold text-zinc-900">{item.units_per_box}</p>
                    </div>
                  )}
                  {item.boxes_per_pallet != null && item.boxes_per_pallet > 0 && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500">Cajas por pallet</p>
                      <p className="font-semibold text-zinc-900">{item.boxes_per_pallet.toLocaleString()}</p>
                    </div>
                  )}
                  {item.boxes_per_container != null && item.boxes_per_container > 0 && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500">Cajas por contenedor</p>
                      <p className="font-semibold text-zinc-900">{item.boxes_per_container.toLocaleString()}</p>
                    </div>
                  )}
                  {item.pallets_per_container != null && item.pallets_per_container > 0 && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500">Pallets por contenedor</p>
                      <p className="font-semibold text-zinc-900">{item.pallets_per_container.toLocaleString()}</p>
                    </div>
                  )}
                  {item.recommended_container_type && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500">Contenedor recomendado</p>
                      <p className="font-semibold text-zinc-900">
                        {item.recommended_container_type === '20DV' && '20ft Dry'}
                        {item.recommended_container_type === '40HQ' && '40ft Dry'}
                        {item.recommended_container_type === '20REF' && '20ft Refer'}
                        {item.recommended_container_type === '40REF' && '40ft Refer'}
                        {!['20DV', '40HQ', '20REF', '40REF'].includes(item.recommended_container_type) && item.recommended_container_type}
                      </p>
                    </div>
                  )}
                  {item.production_time_days != null && (
                    <div className="bg-zinc-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-zinc-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Tiempo de producción
                      </p>
                      <p className="font-semibold text-zinc-900">{item.production_time_days} días</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

            {/* Action Buttons — conventional position, always visible in sticky block */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button variant="default" size="lg" className="gap-2">
                    <Package className="h-4 w-4" />
                    Añadir a cotización
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Cantidad ({moqUnit?.name ?? 'unidades'})</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label>Cantidad (mín. {Math.round(Number(item.moq_value) || 1)} {moqUnit?.name ?? 'unidades'})</Label>
                      <Input
                        type="number"
                        min={Math.round(Number(item.moq_value) || 1)}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button variant="outline" onClick={handleAddToQuotation} className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">Añadir a la orden</Button>
                  </div>
                </DialogContent>
              </Dialog>
              {pendingAdd && (
                <AddToQuotationContainerModal
                  open={containerModalOpen}
                  onOpenChange={setContainerModalOpen}
                  line={pendingAdd.line}
                  item={pendingAdd.item}
                  blocks={request?.blocks}
                  add={add}
                  onSuccess={() => {
                    toast({
                      variant: 'success',
                      title: 'Añadido a la orden de cotización',
                      action: (
                        <ToastAction altText="Ir a la orden de cotización" variant="primary" asChild>
                          <Link to="/store/cart">Ver orden de cotización</Link>
                        </ToastAction>
                      ),
                    });
                    setOpen(false);
                    setPendingAdd(null);
                  }}
                  onError={(msg) => toast({ title: msg ?? 'Error', description: 'No se pudo añadir', variant: 'destructive' })}
                />
              )}
              <Button variant="outline" size="lg" asChild className="gap-2">
                <Link to="/store/cart">
                  <ShoppingCart className="h-4 w-4" />
                  Ver orden
                </Link>
              </Button>
            </div>
          </div>

          {/* Observaciones - Separate Section */}
          {observaciones && (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 border-b border-zinc-200">
                <FileText className="h-4 w-4 text-zinc-500" />
                <h3 className="text-sm font-semibold text-zinc-800">Observaciones</h3>
              </div>
              <div className="px-4 py-3">
                <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{observaciones}</p>
              </div>
            </div>
          )}

          {/* Información de Exportación B2B — GEO: all 47 ports for crawlers and regional relevance */}
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 border-b border-zinc-200">
              <MapPin className="h-4 w-4 text-zinc-500" />
              <h3 className="text-sm font-semibold text-zinc-800">Información de Exportación B2B</h3>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-zinc-600 font-medium">Destinos Frecuentes</p>
              <div className="flex flex-wrap gap-2">
                {ALL_LATAM_PORTS.map((port) => (
                  <span
                    key={port}
                    className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700"
                  >
                    {port}
                  </span>
                ))}
              </div>
              <p className="text-sm text-zinc-600 pt-1">
                Cobertura total a Latinoamérica y el Caribe. Incoterms: FOB, CIF.
              </p>
            </div>
          </div>

          {/* Preguntas Frecuentes (from pSEO ai_faqs) */}
          {seoMeta.ai_faqs && seoMeta.ai_faqs.length > 0 && (
            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 border-b border-zinc-200">
                <FileText className="h-4 w-4 text-zinc-500" />
                <h3 className="text-sm font-semibold text-zinc-800">Preguntas Frecuentes</h3>
              </div>
              <div className="divide-y divide-zinc-100">
                {seoMeta.ai_faqs.map((faq, idx) => (
                  <details key={idx} className="group">
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-50">
                      <span className="flex-1">{faq.pregunta}</span>
                    </summary>
                    <div className="px-4 pb-3 pt-0 text-sm text-zinc-600 leading-relaxed">
                      {faq.respuesta}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreProduct;
